/**
 * /killstreak — SM-5 status screen (V4; S-22 ladder + S-21 token slot),
 * v3 "AFTER-DARK ARCADE": the gold arcade HUD.
 *
 * The current run is a CRT scoreboard — huge Anton counter under gold bloom
 * that ticks on every live update, a combo meter whose cells light in a
 * cascade toward the next tier notch, a ladder whose crossed tiers stay lit,
 * and the takeover-token slots as arcade inventory. Announcer stings ride the
 * §4.3 pushes via the bridge (FIRST BLOOD → GODLIKE, motif gold, combo ticks).
 *
 * Server renders truth (GET /killstreak + pushes); the client never computes
 * a tier or a grant. Also mounts the S-22 announcer bridge.
 */
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { resolveCopy } from "@humble/shared";
import { Card, Skeleton, springs } from "../../design/index.js";
import { api } from "../../lib/api.js";
import { useStore } from "../../lib/store.js";
import { ws } from "../../lib/ws.js";
import { streakStore, useKillstreakAnnouncer } from "./announcer-bridge.js";
import { StreakHud } from "./streak-hud.js";
import "./killstreak.css";

interface KillstreakView {
  streak: { count: number; best: number; tier_reached: number };
  takeover_token: { held: boolean; banked: boolean; claimed: boolean };
  dominating_pack: boolean;
}

/** GET /killstreak is a V4 server route (SM-5 recovery plane); it sits outside
 *  the frozen openapi surface, hence the one-off cast around the typed client. */
async function fetchKillstreak(): Promise<KillstreakView> {
  const get = api.get as unknown as (path: string) => Promise<KillstreakView>;
  return get("/killstreak");
}

const LADDER: Array<{ at: string; tier: number; name: string; grant: string }> = [
  { at: "3", tier: 3, name: "FIRST BLOOD", grant: "Announcer sting + streak flame on your deck HUD." },
  { at: "5", tier: 5, name: "RAMPAGE", grant: "+1 free Boost (consumable — yours even if the streak dies)." },
  { at: "7", tier: 7, name: "DOMINATING", grant: "Exclusive reaction pack (permanent) + deck-card glint." },
  { at: "10", tier: 10, name: "GODLIKE", grant: "+1 Enemy Takeover token (hold 1, bank 1 — never more)." },
  { at: "15+", tier: 15, name: "BEYOND GODLIKE", grant: "Every +5: another token attempt. Caps apply, honestly." },
];

/** Combo meter — 15 cells, notched at the tier thresholds; lit cells cascade
 *  in (pure cosmetics of server state; aria carries the plain number). */
const METER_CELLS = 15;
const NOTCHES = new Set([3, 5, 7, 10, 15]);

function ComboMeter({ count }: { count: number }) {
  const lit = Math.max(0, Math.min(count, METER_CELLS));
  return (
    <div className="ks-meter" role="img" aria-label={`combo meter: ${count}`}>
      {/* key={lit} replays the tick cascade whenever the streak moves */}
      <div className="ks-meter__cells" key={lit} aria-hidden="true">
        {Array.from({ length: METER_CELLS }, (_, i) => {
          const n = i + 1;
          const isLit = n <= lit;
          const isNotch = NOTCHES.has(n);
          return (
            <span
              key={n}
              className={`ks-meter__cell${isLit ? " ks-meter__cell--lit" : ""}${isNotch ? " ks-meter__cell--notch" : ""}`}
              style={isLit ? { animationDelay: `${i * 0.045}s` } : undefined}
            />
          );
        })}
      </div>
      <div className="ks-meter__notches" aria-hidden="true">
        {[3, 5, 7, 10, 15].map((n) => (
          <span key={n} className="ks-meter__notch-label" style={{ left: `${((n - 0.5) / METER_CELLS) * 100}%` }}>
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

export function KillstreakScreen() {
  useKillstreakAnnouncer();
  const live = useStore(streakStore, (s) => s);
  const [view, setView] = useState<KillstreakView | null>(null);

  const load = useCallback(() => {
    fetchKillstreak()
      .then((v) => {
        setView(v);
        streakStore.set({ count: v.streak.count, tierReached: v.streak.tier_reached });
      })
      .catch(() => setView(null));
  }, []);

  useEffect(() => {
    load();
    const offs = [
      ws.on("streak.updated", load),
      ws.on("streak.broken", load),
      ws.on("streak.tier", load),
      ws.on("inventory.updated", load),
    ];
    return () => offs.forEach((off) => off());
  }, [load]);

  const token = view?.takeover_token;
  const hot = live.count > 0;

  return (
    <div className="killstreak-screen">
      <h1 className="type-display-2 killstreak-title">Killstreak</h1>
      <p className="text-dim">
        Consecutive matches — classic or Reject-Match, a match is a match. One mixed resolution and
        the flame dies. The loot you earned stays yours.
      </p>

      {view === null ? (
        <Skeleton style={{ height: 200 }} />
      ) : (
        <>
          <Card padding="lg" glow={hot ? "gold" : "none"} className="killstreak-now crt">
            <div className="killstreak-now__row">
              <div className="killstreak-now__counter">
                <div className={`killstreak-now__big type-numeric${hot ? " killstreak-now__big--hot" : ""}`}>
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span
                      key={live.count}
                      initial={{ y: -22, opacity: 0, scale: 1.4 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      exit={{ y: 18, opacity: 0, scale: 0.7 }}
                      transition={springs.slam}
                      style={{ display: "inline-block" }}
                    >
                      {live.count}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className="killstreak-now__caption h-caps">current streak</div>
                <StreakHud count={live.count} tierReached={live.tierReached} />
              </div>
              <div className="killstreak-now__stats">
                <div className="killstreak-now__statrow">
                  <span className="killstreak-now__stat type-numeric">{view.streak.best}</span>
                  <span className="text-faint"> best run</span>
                </div>
                <div className="killstreak-now__statrow">
                  <span className="killstreak-now__stat type-numeric">{view.streak.tier_reached || "—"}</span>
                  <span className="text-faint"> tier reached this run</span>
                </div>
              </div>
            </div>
            <ComboMeter count={live.count} />
          </Card>

          <h2 className="killstreak-h2">The ladder</h2>
          <div className="killstreak-ladder">
            {LADDER.map((t) => {
              const reached = live.tierReached >= t.tier;
              return (
                <Card
                  key={t.at}
                  padding="md"
                  className={`killstreak-tier${reached ? " killstreak-tier--reached" : ""}`}
                >
                  <span className="killstreak-tier__at type-numeric">{t.at}</span>
                  <div>
                    <div className="killstreak-tier__name">{t.name}</div>
                    <div className="text-dim killstreak-tier__grant">{t.grant}</div>
                  </div>
                </Card>
              );
            })}
          </div>

          <h2 className="killstreak-h2">Enemy Takeover token</h2>
          <Card padding="md">
            <div className="token-slots">
              <div className={`token-slot${token?.held ? " token-slot--full" : ""}`}>
                <div className="token-slot__label">held</div>
                <div className="token-slot__state">{token?.held ? "READY" : "empty"}</div>
              </div>
              <div className={`token-slot${token?.banked ? " token-slot--full" : ""}`}>
                <div className="token-slot__label">banked</div>
                <div className="token-slot__state">{token?.banked ? "STORED" : "empty"}</div>
              </div>
              {token?.claimed && (
                <div className="token-slot token-slot--claimed">
                  <div className="token-slot__label">claimed</div>
                  <div className="token-slot__state">offer pending</div>
                </div>
              )}
            </div>
            <p className="text-faint" style={{ marginBottom: 0 }}>
              {token?.held || token?.banked
                ? "Spend it from any open match conversation. The target decides — consent is the whole game."
                : (resolveCopy("takeover.no_token") ?? "Ten consecutive matches earns a token.")}
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
